import {
  debounce,
  filter,
  find,
  flatMap,
  groupBy,
  map,
  meanBy,
  round,
  toNumber,
} from "lodash";
import React, { useCallback, useMemo } from "react";
import { useParams } from "react-router";
import { Note } from "@/features/workspace/graphql/generated";
import { Column, Row } from "@/shared/ui/Blocks";
import { IconButton } from "@/shared/ui/IconButton";
import { IconName } from "@/shared/ui/Icon";
import { LayoutWithResizableColumns } from "@/shared/widgets/Layout";
import { ScorecardRowWithEvaluation } from "@/features/assessments/components/AssessmentRowGroupsList";
import { ScreenContainer } from "@/shared/ui/ScreenContainer";
import { Title } from "@/shared/ui/Title";
import { EvaluationsSummaryTable } from "@/shared/ui/EvaluationsSummaryTable";
import { Breadcrumbs } from "@/shared/widgets/Breadcrumbs";
import { HeaderContent } from "@/shared/widgets/HeaderContent";
import ProfileHeader from "@/features/profile/components/ProfileHeader";
import { TabNavigation } from "@/shared/widgets/TabNavigation";
import { PDFExportTrigger } from "@/shared/pdf";
import AssessmentReportPdf from "@/shared/pdf/Templates/AssessmentReport";
import { SummarySectionsGroup } from "@/shared/widgets/SummarySectionsGroup";
import { useAccount } from "@/features/workspace/hooks/useAccount";
import { useAssessment } from "@/features/assessments/hooks/useAssessment";
import { useAssessmentNotes } from "@/features/assessments/hooks/useAssessmentNotes";
import { useAssessmentSummaryUpsert } from "@/features/assessments/hooks/useAssessmentSummaryUpsert";
import { useProject } from "@/features/projects/hooks/usePosition";
import { AssessmentRowGroupsList } from "@/features/assessments/components/AssessmentRowGroupsList";
import useOrgNavigate from "@/shared/utils/router/useOrgNavigate";
import { getGptFlowKey } from "@/shared/constants/gpt-flow-types";
import { PPTGeneratorButton } from "@/shared/widgets/PPTGeneratorButton";
import usePerson from "@/features/people/hooks/usePerson";
import { cleanupMarkdownFromTags } from "@/shared/utils/parsers/cleanupMarkdownFromTags";
import { useAssessmentTagsData } from "../hooks/useAssessmentTagsData";

export interface SummarySection {
  title: string;
  type: string;
  gpt_flow_id: string;
  value: string;
}

function AssessmentReport() {
  const {
    personId,
    projectId,
    scorecardId: _scorecardId,
    assessmentId: _assessmentId,
  } = useParams();

  const navigate = useOrgNavigate();

  const assessmentId = _assessmentId;

  const { assessment, evaluations, loading, handePersonEvaluation } =
    useAssessment(toNumber(assessmentId), toNumber(personId));
  const { account } = useAccount();
  const { loading: loadingProject, ...project } = useProject(
    toNumber(projectId)
  );
  const { name, client_name, team_id, org_id } = project;

  // If this is true, all evaluators will edit the same score for a row
  const hasAccessToAI = account?.config?.wisgpt_assessments_enabled || true; // TODO - implement when we have it configured

  const { handleUpsertSummary } = useAssessmentSummaryUpsert(assessment);

  const { person } = usePerson(toNumber(personId));

  const surveyConfig = project?.config?.dimensions;

  const {
    notes,
    loading: notesLoading,
    handleUpserteNote,
  } = useAssessmentNotes(toNumber(assessmentId), toNumber(personId));

  const handleRowEvaluation = useCallback(
    (rowId: number, evaluation: number, meta?: Record<string, any>) => {
      handePersonEvaluation({
        evaluation,
        rowId,
        personId: toNumber(personId),
        meta,
      });
    },
    [handePersonEvaluation, personId]
  );

  const handleRowSummaryEdit = useCallback(
    (note: Note, text: string) => {
      handleUpserteNote({
        ...note,
        id: note.id || undefined,
        assessment_id: toNumber(assessmentId),
        type: "summary",
        text,
        person_id: toNumber(personId),
      });
    },
    [handePersonEvaluation, personId]
  );

  const rowsWithEvaluations = useMemo(
    () =>
      map(assessment.scorecard?.rows, (row) => {
        const note = find(notes, { row_id: row?.id, type: "summary" });
        return {
          ...row,
          evaluations: filter(evaluations, { row_id: row!.id }),
          note,
          summary: note?.text || "",
        };
      }),
    [evaluations, assessmentId, notes, assessment.scorecard?.rows]
  );

  const assessmentRowGroups = useMemo(
    () => groupBy(rowsWithEvaluations, (row) => row?.group_title),
    [rowsWithEvaluations]
  );

  const tagsData = useAssessmentTagsData(project!, assessment.person!);

  const reportData = {
    title: assessment.scorecard?.title,
    candidateName: assessment.person?.name,
    companyName: client_name,
    positionTitle: name,
    candidatePositionRole: `Candidate for the role of ${name} at ${client_name}`,
    evaluationRows: assessmentRowGroups,
    keyStrengths: cleanupMarkdownFromTags(
      find(notes as Note[], { type: "key_strengths_summary" })?.text || ""
    ),
    keyDevelopmentOpportunities: cleanupMarkdownFromTags(
      find(notes as Note[], {
        type: "key_development_opportunities_summary",
      })?.text || ""
    ),
    summary: cleanupMarkdownFromTags(
      find(notes as Note[], { type: "assessment_executive_summary" })?.text ||
        ""
    ),
  };

  const tabs = [
    {
      id: "evaluation",
      label: "AI Evaluation",
      icon: "Magicwand" as IconName,
      path: projectId
        ? `/projects/${projectId}/person/${personId}/assessment/${assessmentId}`
        : `/person/${personId}/assessment/${assessmentId}`,
    },
    {
      id: "report",
      label: "Assessment report",
      isActive: true,
    },
  ];

  const handleSummaryChange = useCallback(
    (type: string, id?: number) =>
      debounce((content: any) => {
        handleUpserteNote({
          id,
          type,
          text: content || "",
        });
      }, 1000),
    [handleUpsertSummary]
  );

  const summarySections = useMemo(() => {
    if (assessment.scorecard?.meta?.summary_fields) {
      return map(assessment.scorecard?.meta?.summary_fields, (field) => ({
        ...field,
        value: find(notes as Note[], { type: field.type })?.text,
      }));
    }
    return [
      {
        title: "Executive Summary",
        type: "assessment_executive_summary",
        gpt_flow_id: getGptFlowKey("assessment_summary"),
        value: find(notes as Note[], { type: "assessment_executive_summary" })
          ?.text,
      },
    ];
  }, [assessment.scorecard?.meta, notes]);

  return (
    <LayoutWithResizableColumns
      columnsGrow={[70, 30]}
      columnsMinSize={[50, 20]}
    >
      <ScreenContainer
        fullWidth
        contentClassName="flex-1 px-0!  space-y-8 pt-0!"
        loading={loading || !assessment.id}
      >
        <Breadcrumbs
          items={[
            { label: name || "", path: `/projects/${projectId}` },
            {
              label: assessment?.person?.name || "",
              path: `/projects/${projectId}/person/${personId}`,
            },
            {
              label: "Assessment report",
            },
          ]}
        />
        <HeaderContent>
          <PDFExportTrigger
            documentTitle={`${name} - ${assessment.person?.name} - Assessment report`}
            TriggerComponent={(props) => (
              <IconButton
                color="black"
                icon="Print"
                label="Print report"
                variant="outline"
                {...props}
              />
            )}
            PDFComponent={
              <AssessmentReportPdf
                profile={person}
                assessment={assessment}
                assessmentRowGroups={assessmentRowGroups}
                summarySections={summarySections}
              />
            }
          />
          <PPTGeneratorButton
            templateName="rra-individual-assessment"
            data={reportData}
            fileName={`${assessment.scorecard?.title}-${assessment.person?.name}.pptx`}
          />
        </HeaderContent>
        {!!hasAccessToAI && (
          <TabNavigation
            tabs={tabs}
            className="sticky px-6 top-0 bg-white z-10"
            RightComponent={() => (
              <Row className="flex-1 justify-end">
                <IconButton
                  icon="Chevron"
                  size="md"
                  label="Back to assessments"
                  // variant="outline"
                  className="flex-row-reverse"
                  // color="black"
                  onClick={() => {
                    navigate(`/projects/${projectId}`);
                  }}
                />
              </Row>
            )}
          />
        )}
        <Column className="max-w-(--breakpoint-xl) p-4 w-full self-center overflow-y-auto pb-24">
          <ProfileHeader
            avatarUrl={person.avatar || ""}
            name={person.name || ""}
            initials={person.initials || ""}
            className="mb-8"
            teamName={person.email || ""}
            canEdit={false}
          />
          <Column className="mb-16 space-y-8">
            <EvaluationsSummaryTable
              rows={map(assessmentRowGroups, (group, title) => ({
                id: title,
                title,
                evaluation: round(
                  meanBy(
                    flatMap(group, (r) => r.evaluations),
                    "evaluation"
                  )
                ),
              }))}
            />
            <SummarySectionsGroup
              sections={summarySections}
              notes={notes as Note[]}
              onDeleteSummary={handleSummaryChange}
              onSaveSummary={handleSummaryChange}
              tagsData={tagsData}
              scope={{
                person_id: toNumber(personId),
                team_id: team_id ? toNumber(team_id) : undefined,
                project_id: toNumber(projectId),
                scorecard_id: toNumber(_scorecardId),
                org_id: org_id ? toNumber(org_id) : undefined,
                assessment_id: toNumber(assessmentId),
                survey_config: surveyConfig,
              }}
              loading={loading || loadingProject}
              useSingleGenerator={summarySections.length > 1}
            />
          </Column>

          <Column className="mb-8">
            <Title as="h3" icon="Score" color="blue">
              Assessment Scores
            </Title>
          </Column>

          <AssessmentRowGroupsList
            variant="report"
            groups={
              assessmentRowGroups as Record<
                string,
                ScorecardRowWithEvaluation[]
              >
            }
            onEvaluate={handleRowEvaluation}
            onSummaryEdit={handleRowSummaryEdit}
          />
        </Column>
      </ScreenContainer>
    </LayoutWithResizableColumns>
  );
}

export default AssessmentReport;
